import $ from "jquery";
import _ from "lodash";
import assert from "minimalistic-assert";
import {z} from "zod";

import * as common from "../common.ts";
import {$t} from "../i18n.ts";
import {password_quality, password_warning} from "../password_quality.ts";
import * as settings_config from "../settings_config.ts";

import * as portico_modals from "./portico_modals.ts";

$(() => {
    // NB: this file is included on multiple pages.  In each context,
    // some of the jQuery selectors below will return empty lists.

    const $password_field = $<HTMLInputElement>("input#id_password, input#id_new_password1");
    if ($password_field.length > 0) {
        $.validator.addMethod(
            "password_strength",
            (value: string) => password_quality(value, undefined, $password_field),
            () => password_warning($password_field.val()!, $password_field),
        );
        // Reset the state of the password strength bar if the page
        // was just reloaded due to a validation failure on the backend.
        password_quality($password_field.val()!, $("#pw_strength .bar"), $password_field);

        const debounced_password_quality = _.debounce((password_value: string, $field: JQuery) => {
            password_quality(password_value, $("#pw_strength .bar"), $field);
        }, 300);

        $password_field.on("input", function () {
            const password_value = $(this).val()!;

            if (password_value.length > 30) {
                debounced_password_quality(password_value, $(this));
            } else {
                debounced_password_quality.cancel();
                password_quality(password_value, $("#pw_strength .bar"), $(this));
            }
        });
    }

    common.setup_password_visibility_toggle(
        "#ldap-password",
        "#ldap-password ~ .password_visibility_toggle",
    );
    common.setup_password_visibility_toggle(
        "#id_password",
        "#id_password ~ .password_visibility_toggle",
    );
    common.setup_password_visibility_toggle(
        "#id_new_password1",
        "#id_new_password1 ~ .password_visibility_toggle",
    );
    common.setup_password_visibility_toggle(
        "#id_new_password2",
        "#id_new_password2 ~ .password_visibility_toggle",
    );

    $("#registration, #password_reset, #create_realm").validate({
        rules: {
            password: "password_strength",
            new_password1: "password_strength",
        },
        errorElement: "p",
        errorPlacement($error: JQuery, $element: JQuery) {
            // NB: this is called at most once, when the error element
            // is created.
            $element.next(".help-inline.alert.alert-error").remove();
            if ($element.next().is(`label[for="${$element.attr("id")!}"]`)) {
                $error.insertAfter($element.next()).addClass("help-inline alert alert-error");
            } else if ($element.parent().is(`label[for="${$element.attr("id")!}"]`)) {
                // For checkboxes and radio-buttons
                $error.insertAfter($element.parent()).addClass("help-inline alert alert-error");
            } else {
                $error.insertAfter($element).addClass("help-inline alert alert-error");
            }
        },
    });

    if ($("#registration").length > 0) {
        // Check if there is no input field with errors.
        if ($(".help-inline:not(:empty)").length === 0) {
            // Find the first input field present in the form that is
            // not hidden and disabled and store it in a variable.
            const $firstInputElement = $("input:not(:hidden, :disabled)").first();
            // Focus on the first input field in the form.
            $firstInputElement.trigger("focus");
            // Override the automatic scroll to the focused
            // element. On the (tall) new organization form, at least,
            // this avoids scrolling to the middle of the page (past
            // the organization details section).
            $("html").scrollTop(0);
        } else {
            // If input field with errors is present.
            // Find the input field having errors and stores it in a variable.
            const $inputElementWithError = $(".help-inline:not(:empty)")
                .first()
                .parent()
                .find("input");
            // Focus on the input field having errors.
            $inputElementWithError.trigger("focus");
        }

        // reset error message displays
        $("#id_team_subdomain_error_client").css("display", "none");
        if ($(".team_subdomain_error_server").text() === "") {
            $(".team_subdomain_error_server").css("display", "none");
        }

        $("#timezone").val(new Intl.DateTimeFormat().resolvedOptions().timeZone);
    }

    $("#registration").on("submit", () => {
        if ($("#registration").valid()) {
            $(".register-button .loader").css("display", "inline-block");
            $(".register-button").prop("disabled", true);
            $(".register-button span").hide();
        }
    });

    // Code in this block will be executed when the /accounts/send_confirm/
    // endpoint is visited i.e. accounts_send_confirm.html is rendered.
    if ($("[data-page-id='accounts-send-confirm']").length > 0) {
        $("#resend_email_link").on("click", () => {
            $(".resend_confirm").trigger("submit");
        });

        // PIN code functionality
        const pinDigits = document.querySelectorAll('.pin-digit');
        const pinForm = document.getElementById('pin-code-form') as HTMLFormElement;
        const submitBtn = document.querySelector('.pin-submit-btn') as HTMLButtonElement;
        const errorDiv = document.getElementById('pin-error') as HTMLDivElement;

        // Function to check if all PIN digits are filled
        function checkPinComplete(): boolean {
            return Array.from(pinDigits).every(input => (input as HTMLInputElement).value.length === 1);
        }

        // Function to enable/disable submit button
        function updateSubmitButton(): void {
            if (submitBtn) {
                submitBtn.disabled = !checkPinComplete();
            }
        }

        // Function to show error message
        function showError(message: string): void {
            if (errorDiv) {
                // Use innerHTML instead of textContent for proper translation rendering
                errorDiv.innerHTML = message;
                errorDiv.classList.add('show');
                // Add error class to all digits
                pinDigits.forEach(input => {
                    const inputElement = input as HTMLInputElement;
                    inputElement.classList.remove('filled', 'success');
                    inputElement.classList.add('error');
                });
                // Remove error class after animation
                setTimeout(() => {
                    pinDigits.forEach(input => (input as HTMLInputElement).classList.remove('error'));
                    updateDigitStates();
                }, 600);
            }
        }

        // Helper function to get translated text from hidden elements
        function getTranslatedText(elementId: string): string {
            const element = document.getElementById(elementId);
            return element ? element.textContent || '' : '';
        }

        // Function to hide error message
        function hideError(): void {
            if (errorDiv) {
                errorDiv.classList.remove('show');
            }
        }

        // Function to update visual state of digits
        function updateDigitStates(): void {
            pinDigits.forEach(input => {
                const inputElement = input as HTMLInputElement;
                // Remove all state classes first
                inputElement.classList.remove('filled', 'success', 'error');
                
                // Add filled class only if input has value
                if (inputElement.value) {
                    inputElement.classList.add('filled');
                }
            });
        }

        // Function to show success state
        function showSuccess(): void {
            pinDigits.forEach(input => {
                const inputElement = input as HTMLInputElement;
                inputElement.classList.remove('filled', 'error');
                inputElement.classList.add('success');
            });
        }

        // Add event listeners to each PIN digit input
        pinDigits.forEach((input, index) => {
            const inputElement = input as HTMLInputElement;
            
            // Handle beforeinput event to always replace existing content
            inputElement.addEventListener('beforeinput', (e) => {
                const target = e.target as HTMLInputElement;
                const inputType = e.inputType;
                
                // For insertText and insertCompositionText, always select all existing content
                // so new input replaces it completely
                if (inputType === 'insertText' || inputType === 'insertCompositionText') {
                    if (target.value) {
                        target.select();
                    }
                }
            });
            
            inputElement.addEventListener('input', (e) => {
                const target = e.target as HTMLInputElement;
                const value = target.value;
                
                // If multiple characters were entered (paste or rapid input)
                if (value.length > 1) {
                    // Extract only digits from the pasted content
                    const digits = value.replace(/\D/g, '');
                    
                    if (digits.length > 0) {
                        // Fill current and subsequent inputs with digits
                        const remainingSlots = pinDigits.length - index;
                        const digitsToUse = digits.slice(0, remainingSlots);
                        
                        // Clear current input first
                        target.value = '';
                        
                        // Fill current and next inputs
                        for (let i = 0; i < digitsToUse.length && (index + i) < pinDigits.length; i++) {
                            const digit = digitsToUse[i];
                            if (digit !== undefined) {
                                (pinDigits[index + i] as HTMLInputElement).value = digit;
                            }
                        }
                        
                        // Focus the next empty input or the last filled input
                        const nextFocusIndex = Math.min(index + digitsToUse.length, pinDigits.length - 1);
                        (pinDigits[nextFocusIndex] as HTMLInputElement).focus();
                    } else {
                        // No valid digits, clear the input
                        target.value = '';
                    }
                } else if (value.length === 1) {
                    // Single character input - only allow digits
                    if (!/^\d$/.test(value)) {
                        target.value = '';
                        return;
                    }
                    
                    // Auto-focus next input only if we just entered a digit
                    if (index < pinDigits.length - 1) {
                        (pinDigits[index + 1] as HTMLInputElement).focus();
                    }
                }
                
                hideError();
                updateDigitStates();
                updateSubmitButton();
            });
            
            inputElement.addEventListener('keydown', (e) => {
                const target = e.target as HTMLInputElement;
                
                // Handle backspace
                if (e.key === 'Backspace' && !target.value && index > 0) {
                    (pinDigits[index - 1] as HTMLInputElement).focus();
                }
                
                // Handle arrow keys
                if (e.key === 'ArrowLeft' && index > 0) {
                    e.preventDefault();
                    (pinDigits[index - 1] as HTMLInputElement).focus();
                }
                if (e.key === 'ArrowRight' && index < pinDigits.length - 1) {
                    e.preventDefault();
                    (pinDigits[index + 1] as HTMLInputElement).focus();
                }
                
                // Handle paste will be handled separately with paste event
            });
            
            // Handle paste event separately for better reliability
            inputElement.addEventListener('paste', (e) => {
                e.preventDefault();
                
                const paste = e.clipboardData?.getData('text') || '';
                const digits = paste.replace(/\D/g, '').slice(0, 6);
                
                if (digits.length > 0) {
                    // Fill all PIN digits starting from current position
                    pinDigits.forEach((input, i) => {
                        (input as HTMLInputElement).value = digits[i] || '';
                    });
                    
                    hideError();
                    updateDigitStates();
                    updateSubmitButton();
                    
                    // Focus the next empty input or the last input if all are filled
                    const nextEmptyIndex = Math.min(digits.length, pinDigits.length - 1);
                    (pinDigits[nextEmptyIndex] as HTMLInputElement).focus();
                }
            });
            
            // Prevent non-numeric input
            inputElement.addEventListener('keypress', (e) => {
                if (!/\d/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter'].includes(e.key)) {
                    e.preventDefault();
                }
            });
            
            // Handle focus/blur events for visual feedback
            inputElement.addEventListener('focus', () => {
                // Don't hide error on focus - only hide when user starts typing
                // Select all text when field gets focus for easy replacement
                inputElement.select();
            });
            
            inputElement.addEventListener('blur', () => {
                updateDigitStates();
            });
        });

        // Handle form submission
        if (pinForm) {
            pinForm.addEventListener('submit', async (e) => {
                e.preventDefault();
                
                const pinCode = Array.from(pinDigits)
                    .map(input => (input as HTMLInputElement).value)
                    .join('');
                
                if (pinCode.length !== 6) {
                    showError(getTranslatedText('pin-error-digits'));
                    return;
                }
                
                // Disable submit button during request
                if (submitBtn) {
                    submitBtn.disabled = true;
                    submitBtn.textContent = getTranslatedText('pin-verifying');
                }
                
                try {
                    const formData = new FormData(pinForm);
                    formData.append('pin_code', pinCode);
                    
                    const response = await fetch('/accounts/verify_pin/', {
                        method: 'POST',
                        body: formData,
                        headers: {
                            'X-Requested-With': 'XMLHttpRequest',
                        },
                    });
                    
                    if (!response.ok) {
                        // Handle HTTP errors (404, 500, etc.)
                        if (response.status === 404) {
                            showError(getTranslatedText('pin-error-404'));
                        } else {
                            showError(`Server error (${response.status}). Please try again later.`);
                        }
                        // Clear PIN inputs
                        pinDigits.forEach(input => {
                            const inputElement = input as HTMLInputElement;
                            inputElement.value = '';
                        });
                        updateDigitStates();
                        (pinDigits[0] as HTMLInputElement).focus();
                        return;
                    }
                    
                    const data = await response.json();
                    
                    if (data.success) {
                        // Success animation
                        showSuccess();
                        
                        submitBtn.textContent = getTranslatedText('pin-verified');
                        
                        // Redirect to next step after brief delay
                        setTimeout(() => {
                            window.location.href = data.redirect_url || '/accounts/register/';
                        }, 800);
                    } else {
                        // Use specific error messages based on the error content
                        let errorMessage = data.error || getTranslatedText('pin-error-invalid');
                        
                        // Map specific error messages to translated versions
                        if (errorMessage.includes('check your email')) {
                            errorMessage = getTranslatedText('pin-error-invalid-email');
                        } else if (errorMessage.includes('expired')) {
                            errorMessage = getTranslatedText('pin-error-expired');
                        } else if (errorMessage.includes('error occurred while verifying')) {
                            errorMessage = getTranslatedText('pin-error-general-verify');
                        } else if (errorMessage.includes('Invalid PIN code')) {
                            errorMessage = getTranslatedText('pin-error-invalid-email');
                        }
                        
                        showError(errorMessage);
                        // Clear PIN inputs
                        pinDigits.forEach(input => {
                            const inputElement = input as HTMLInputElement;
                            inputElement.value = '';
                        });
                        updateDigitStates();
                        (pinDigits[0] as HTMLInputElement).focus();
                    }
                } catch (error) {
                    // Handle network errors
                    if (error instanceof TypeError && error.message.includes('fetch')) {
                        showError(getTranslatedText('pin-error-network'));
                    } else {
                        showError(getTranslatedText('pin-error-general'));
                    }
                    // Clear PIN inputs
                    pinDigits.forEach(input => {
                        const inputElement = input as HTMLInputElement;
                        inputElement.value = '';
                    });
                    updateDigitStates();
                    (pinDigits[0] as HTMLInputElement).focus();
                } finally {
                    // Re-enable submit button
                    if (submitBtn && submitBtn.textContent === getTranslatedText('pin-verifying')) {
                        submitBtn.textContent = getTranslatedText('pin-verify');
                        updateSubmitButton();
                    }
                }
            });
        }

        // Focus first input on page load
        if (pinDigits.length > 0) {
            (pinDigits[0] as HTMLInputElement).focus();
        }
    }

    // Code in this block will be executed when the user visits /register
    // i.e. accounts_home.html is rendered.
    if ($("[data-page-id='accounts-home']").length > 0 && window.location.hash.startsWith("#")) {
        document.querySelector<HTMLFormElement>("#send_form")!.action += window.location.hash;
    }

    // Code in this block will be executed when the user is at login page
    // i.e. login.html is rendered.
    if ($("[data-page-id='login-page']").length > 0 && window.location.hash.startsWith("#")) {
        // All next inputs have the same value when the page is
        // rendered, so it's OK that this selector gets N elements.
        const next_value = $("input[name='next']").attr("value");

        // We need to add `window.location.hash` to the `next`
        // property, since the server doesn't receive URL fragments
        // (and thus could not have included them when rendering a
        // redirect to this page).
        $("input[name='next']").attr("value", `${next_value!}${window.location.hash}`);
    }

    $("#send_confirm").validate({
        errorElement: "div",
        errorPlacement($error: JQuery) {
            $(".email-frontend-error").empty();
            $("#send_confirm .alert.email-backend-error").remove();
            $error.appendTo($(".email-frontend-error")).addClass("text-error");
        },
        success() {
            $("#errors").empty();
        },
    });

    $<HTMLInputElement>(".register-page input#email, .login-page-container input#id_username").on(
        "focusout keydown",
        function (e) {
            // check if it is the "focusout" or if it is a keydown, then check
            // if the key was "Enter"
            if (e.type === "focusout" || e.key === "Enter") {
                $(this).val($(this).val()!.trim());
            }
        },
    );

    const show_subdomain_section = function (bool: boolean): void {
        const action = bool ? "hide" : "show";
        $("#subdomain_section")[action]();
    };

    $("#realm_in_root_domain").on("change", function () {
        show_subdomain_section($(this).is(":checked"));
    });

    $("#login_form").validate({
        errorClass: "text-error",
        wrapper: "div",
        submitHandler(form) {
            $("#login_form").find(".loader").css("display", "inline-block");
            $("#login_form").find("button .text").hide();

            form.submit();
        },
        invalidHandler() {
            // this removes all previous errors that were put on screen
            // by the server.
            $("#login_form .alert.alert-error").remove();
            $("#server-login-error").remove();
        },
        showErrors(error_map) {
            if (error_map.password) {
                $("#login_form .alert.alert-error").remove();
            }
            this.defaultShowErrors!();
        },
    });

    // Hide server error when user starts typing in password field
    $("#id_password").on("input", () => {
        $("#server-login-error").remove();
    });

    function check_subdomain_available(subdomain: string): void {
        const url = "/json/realm/subdomain/" + subdomain;
        void $.get(url, (response) => {
            const {msg} = z.object({msg: z.string()}).parse(response);
            if (msg !== "available") {
                $("#id_team_subdomain_error_client").html(msg);
                $("#id_team_subdomain_error_client").show();
            }
        });
    }

    function update_full_name_section(): void {
        if ($("#source_realm_select").length > 0 && $("#source_realm_select").val() !== "") {
            $("#full_name_input_section").hide();
            $("#profile_info_section").show();
            const avatar_url = $($("#source_realm_select").prop("selectedOptions")).attr(
                "data-avatar",
            );
            const full_name = $($("#source_realm_select").prop("selectedOptions")).attr(
                "data-full-name",
            );
            $("#profile_full_name").text(full_name!);
            $("#id_full_name").val(full_name!);
            $("#profile_avatar").attr("src", avatar_url!);
        } else {
            $("#full_name_input_section").show();
            $("#profile_info_section").hide();
        }
    }

    $("#source_realm_select").on("change", update_full_name_section);
    update_full_name_section();

    let timer: number;
    $("#id_team_subdomain").on("input", () => {
        $(".team_subdomain_error_server").text("").css("display", "none");
        $("#id_team_subdomain_error_client").css("display", "none");
        clearTimeout(timer);
        timer = setTimeout(check_subdomain_available, 250, $("#id_team_subdomain").val());
    });

    // GitHub auth
    $("body").on("click", "#choose_email .choose-email-box", function (this: HTMLElement) {
        assert(this.parentElement instanceof HTMLFormElement);
        this.parentElement.submit();
    });

    $("#new-user-email-address-visibility .change_email_address_visibility").on("click", () => {
        portico_modals.open("change-email-address-visibility-modal");
    });

    $("#change-email-address-visibility-modal .dialog_submit_button").on("click", () => {
        const selected_val = Number.parseInt(
            $<HTMLSelectElement & {type: "select-one"}>(
                "select:not([multiple])#new_user_email_address_visibility",
            ).val()!,
            10,
        );
        $("#email_address_visibility").val(selected_val);
        portico_modals.close("change-email-address-visibility-modal");

        let selected_option_text;

        // These strings should be consistent with those defined for the same element in
        // 'templates/zerver/register.html'.
        switch (selected_val) {
            case settings_config.email_address_visibility_values.admins_only.code: {
                selected_option_text = $t({
                    defaultMessage:
                        "Administrators of this Zulip organization will be able to see this email address.",
                });

                break;
            }
            case settings_config.email_address_visibility_values.moderators.code: {
                selected_option_text = $t({
                    defaultMessage:
                        "Administrators and moderators this Zulip organization will be able to see this email address.",
                });

                break;
            }
            case settings_config.email_address_visibility_values.nobody.code: {
                selected_option_text = $t({
                    defaultMessage:
                        "Nobody in this Zulip organization will be able to see this email address.",
                });

                break;
            }
            default: {
                selected_option_text = $t({
                    defaultMessage:
                        "Other users in this Zulip organization will be able to see this email address.",
                });
            }
        }
        $("#new-user-email-address-visibility .current-selected-option").text(selected_option_text);
    });

    $("#registration").on("click keypress", ".edit-realm-details", (e) => {
        if (e.type === "keypress" && e.key !== "Enter") {
            return;
        }

        $("#registration .not-editable-realm-details").addClass("hide");
        $("#registration .realm-creation-editable-inputs").removeClass("hide");
        $("#id_team_name").trigger("focus");
        // This is a hack to have cursor at end after focussing the input.
        const name_val = $("#id_team_name").val()!;
        $("#id_team_name").val("").val(name_val);

        $(e.target).hide();
    });

    $<HTMLSelectElement>("#how-realm-creator-found-zulip select").on("change", function () {
        const elements = new Map([
            ["other", "how-realm-creator-found-zulip-other"],
            ["ad", "how-realm-creator-found-zulip-where-ad"],
            ["existing_user", "how-realm-creator-found-zulip-which-organization"],
            ["review_site", "how-realm-creator-found-zulip-review-site"],
        ]);

        const hideElement = (element: string): void => {
            const $element = $(`#${CSS.escape(element)}`);
            $element.hide();
            $element.removeAttr("required");
            $(`#${CSS.escape(element)}-error`).hide();
        };

        const showElement = (element: string): void => {
            const $element = $(`#${CSS.escape(element)}`);
            $element.show();
            $element.attr("required", "required");
        };

        // Reset state
        for (const element of elements.values()) {
            if (element) {
                hideElement(element);
            }
        }

        // Show the additional input box if needed.
        const selected_element = elements.get(this.value);
        if (selected_element !== undefined) {
            showElement(selected_element);
        }
    });
});
